/**
 * Featured Quiz Card
 *
 * Display card for ready-to-play curated quizzes with 1-click launch.
 */

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Play, Clock } from "lucide-react";
import { getCountryFlag } from "@/utils/countryFlags";
import type { FeaturedQuizConfig } from "@/data/featuredQuizzes";

interface FeaturedQuizCardProps {
  quiz: FeaturedQuizConfig;
  onPlay: (quiz: FeaturedQuizConfig) => void;
}

const DIFFICULTY_COLORS = {
  beginner: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  intermediate:
    "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  advanced: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const CATEGORY_META = {
  phone: { emoji: "📱", label: "Phone Codes" },
  admin: { emoji: "🗺️", label: "Divisions" },
  cities: { emoji: "🏙️", label: "Cities" },
  custom: { emoji: "📁", label: "Custom" },
};

export function FeaturedQuizCard({ quiz, onPlay }: FeaturedQuizCardProps) {
  // Extract geography from dataset label (e.g., "United States · States")
  const geography = quiz.datasetLabel.split("·")[0]?.trim() || "";
  const flag = getCountryFlag(geography);
  const categoryMeta = CATEGORY_META[quiz.category];

  return (
    <Card className="group hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer border-2 hover:border-primary/50">
      <CardContent className="p-6 space-y-4">
        {/* Header with Flag and Title */}
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-lg flex-1">
              <span className="text-2xl mr-2">{flag}</span>
              {quiz.title}
            </h3>
            <Badge variant="secondary" className={DIFFICULTY_COLORS[quiz.difficulty]}>
              {quiz.difficulty}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {quiz.description}
          </p>
        </div>

        {/* Metadata */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <span>{categoryMeta.emoji}</span>
            <span>{categoryMeta.label}</span>
          </div>
          <div>•</div>
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{quiz.estimatedTime}</span>
          </div>
          <div>•</div>
          <div>
            <span className="font-medium">{quiz.featureCount}</span> questions
          </div>
        </div>

        {/* Play Button */}
        <Button
          size="sm"
          className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            onPlay(quiz);
          }}
        >
          <Play className="w-4 h-4 mr-2" />
          Start Quiz
        </Button>
      </CardContent>
    </Card>
  );
}
